<template>
  <section>
    <e-filters
      ref="filters"
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="2">
            <FormulateInput
              id="purchase_requisition_id"
              v-model="filters.id"
              type="number"
              :label="$t('Id da requisição')"
            />
          </b-col>
          <b-col md="3">
            <e-store-combo
              id="store-id"
              v-model="filters.storeId"
              :required="false"
            />
          </b-col>
          <b-col md="2">
            <FormulateInput
              id="status"
              v-model="filters.status"
              type="vue-select"
              :label="$t('Status')"
              :options="[{ label: 'Todos', value: '' }, ...mxPurchaseRequisitionStatusOptions()]"
              :clearable="false"
            />
          </b-col>

          <b-col md="4">
            <e-search-sku
              id="product-name"
              v-model="filters.skuId"
              :required="false"
            />
          </b-col>

          <b-col md="4">
            <FormulateInput
              id="invoice_date"
              v-model="filters.createdDate"
              type="date-range-picker"
              :label="$t('Data de criação')"
              :time-picker="false"
            />
          </b-col>
        </b-row>
      </div>

      <template #after_buttons>
        <e-button
          class="ml-1"
          :busy="fetching"
          :text="$t('Baixar Relatório')"
          variant="primary"
          icon="download"
          @click="downloadReport"
        />
      </template>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="getData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="purchaseRequisitionTable"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('PURCHASE_ORDER.NAME'), 'male', queryExecuted)"
        :fields="fields"
        :items="purchaseRequisitions"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(action)="row">
          <e-grid-actions
            :show-update="showUpdate(row.item)"
            :show-delete="false"
            :buttons="gridButtons(row.item)"
            @update="showUpdatePage(row.item)"
            @delete="showCancelPurchaseRequisition(row.item)"
            @details="showDetailsPage(row.item)"
            @return-items="showModalReturnItems(row)"
          />
        </template>

        <template #cell(status)="row">
          <e-status-badge
            :status="row.item.status"
            :options="mxPurchaseRequisitionStatusOptions()"
          />
        </template>
      </b-table>
      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="order-purchase-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <modal-cancel-purchase-requisition
      ref="modalCancel"
      @after-confirm="getData"
    />

    <!-- v-if="$can('Create', 'Product')" -->
    <fab
      :main-tooltip="$t('Novo Pedido')"
      :fixed-tooltip="true"
      @click="showCreatePage"
    />
  </section>
</template>

<script>
import moment from 'moment'
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EFilters,
  EGridActions,
  EStoreCombo,
  ESearchSku,
  EButton,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import { downloader, purchaseRequisitionDomains } from '@/mixins'
import { mapActions, mapGetters, mapState } from 'vuex'
import EStatusBadge from '@/views/components/EStatusBadge.vue'
import ModalCancelPurchaseRequisition from './components/ModalCancelPurchaseRequisition.vue'

export default {
  components: {
    BRow,
    BCol,
    BCardActions,
    BPagination,
    BTable,
    EPaginationSummary,
    EPageSizeSelector,
    fab,
    EFilters,
    EGridActions,
    EStoreCombo,
    ESearchSku,
    EButton,
    ModalCancelPurchaseRequisition,
    EStatusBadge,
  },

  mixins: [downloader, purchaseRequisitionDomains],

  data() {
    return {
      queryExecuted: false,
      fetching: false,
      suppliers: [],
    }
  },

  computed: {
    ...mapState('pages/purchase/purchaseRequisition', {
      purchaseRequisitions: 'purchaseRequisitions',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),
    ...mapGetters('app', ['storeOptions']),

    showUpdate() {
      return purchaseRequisition => !this.mxIsPurchaseRequisitionReadOnly(purchaseRequisition)
    },
    showCancel() {
      return purchaseRequisition => !!purchaseRequisition
    },

    gridButtons() {
      return purchaseRequisition => {
        if (this.mxIsPurchaseRequisitionReadOnly(purchaseRequisition)) {
          return [
            {
              icon: 'search',
              event: 'details',
              variant: 'outline-primary',
              title: this.$t('Ver detalhes'),
            },
          ].filter(btn => !btn.hide)
        }

        return []
      }
    },
    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          tdClass: 'text-right',
          thStyle: { width: '80px' },
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'status',
          class: 'text-center',
          thStyle: { width: '170px' },
          sortable: true,
        },
        {
          label: this.$t('Loja'),
          key: 'store',
          formatter: value => this.$options.filters.storeName(value),
        },
        {
          label: this.$t('Origem'),
          key: 'origin',
          class: 'text-center',
          thStyle: { width: '130px' },
          formatter: value => (value ? this.$t(`PurchaseRequisition.Origin.${value}`) : '-'),
        },
        {
          label: this.$t('Grupo'),
          key: 'group',
          thStyle: { width: '180px' },
          formatter: value => value?.name || '-',
        },
        {
          label: this.$t('Data'),
          key: 'createdDate',
          class: 'text-center',
          formatter: value => this.$options.filters.date(value),
          thStyle: { width: '150px' },
          sortable: true,
        },
        {
          label: this.$t('Qtd. Itens'),
          key: 'totalItems',
          tdClass: 'text-right',
          thStyle: { width: '160px' },
          sortable: true,
        },
      ]
    },

    actions() {
      return []
    },
  },

  mounted() {},

  methods: {
    ...mapActions('pages/purchase/purchaseRequisition/purchaseRequisitionMaintain', {
      cleanPurchaseRequisitionMaintain: 'cleanState',
    }),
    ...mapActions('pages/purchase/purchaseRequisition', [
      'fetchPurchaseRequisition',
      'generateReport',
      'setFilter',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchPurchaseRequisition()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    async downloadReport() {
      try {
        if (this.$refs.filters.isValid()) {
          this.fetching = true
          const data = await this.generateReport()

          if (data) {
            this.forceFileDownloadXlsx(data, `Requisicoes_de_Compra_${moment().format()}`)
          } else {
            this.showError({ message: this.$t('Não foi possível recuperar os dados do relatório') })
          }
        }
      } catch (error) {
        this.showError({ error })
      } finally {
        this.fetching = false
      }
    },

    async showCancelPurchaseRequisition(purchaseRequisition) {
      this.$refs.modalCancel.showModal(purchaseRequisition)
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.getData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCreatePage() {
      this.cleanPurchaseRequisitionMaintain()
      this.$router.push({ name: 'purchase-requisition-add' })
    },

    async showUpdatePage(purchaseRequisition) {
      const { id } = purchaseRequisition
      this.$router.push({
        name: 'purchase-requisition-maintain',
        params: { id },
      })
    },

    async showDetailsPage(purchaseRequisition) {
      const { id } = purchaseRequisition
      this.$router.push({
        name: 'purchase-requisition-details',
        params: { id },
      })
    },
  },
}
</script>

<style></style>
